import { Controller } from '@hotwired/stimulus';

const availableModalControllers = [];
const MODALS_STORAGE_KEY = 'modals_shown_dates';

export default class extends Controller {
    connect() {
        availableModalControllers.push(this);

        this._onElementOpen = () => this.showModal();
        this._onElementClose = () => this.hideModal();
        this.element.addEventListener('modal:open', this._onElementOpen);
        this.element.addEventListener('modal:close', this._onElementClose);

        this._onDocumentOpen = (event) => {
            const id = event?.detail?.id ?? event?.detail?.modalId ?? null;
            if (!id) return;
            if (id === this.element.id) this.showModal();
        };

        this._onDocumentClose = (event) => {
            const id = event?.detail?.id ?? event?.detail?.modalId ?? null;
            if (!id) return;
            if (id === this.element.id) this.hideModal();
        };

        document.addEventListener('modal:open', this._onDocumentOpen);
        document.addEventListener('modal:close', this._onDocumentClose);

        this.modalScrollContainer = this.element.querySelector('.modal-scroll-container');
        this.modalDialogContainer = this.element.querySelector('.modal-dialog');
        this.modalContent = this.element.querySelector('.modal-content');
        this.closeOnClick = this.element.dataset.closeOnClick === 'true';
        this.autoOpen = this.element.dataset.autoOpen === 'true';
        this.modalScrollButtons = this.element.querySelector('.modal-scroll-buttons');

        if (this.autoOpen) {
            setTimeout(() => this.showModal(), 1000);
            this.stripUrlParams();
        }

        for (const close of this.element.querySelectorAll('.modal-close')) {
            close.addEventListener('click', () => this.hideModal());
        }

        if (this.closeOnClick) {
            this.modalScrollContainer.addEventListener('mousedown', (e) => {
                if (e.target === this.modalScrollContainer) this.hideModal();
            });

            this.modalDialogContainer.addEventListener('mousedown', (e) => {
                if (e.target === this.modalDialogContainer) this.hideModal();
            });
        }

        this.modalContent.addEventListener('click', (e) => e.stopPropagation());

        if (this.modalScrollButtons) {
            this.modalScrollButtons.querySelectorAll('.modal-scroll-button').forEach((el) => {
                el.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.scrollModal(el);
                });
            });
        }

        this.bindModalOpenButtons();
    }

    scrollModal(el) {
        const direction = el.dataset.direction;

        if (direction === 'up') {
            this.modalScrollContainer.scrollTop -= 500;
        } else if (direction === 'down') {
            this.modalScrollContainer.scrollTop += 500;
        }
    }


    stripUrlParams() {
        const params = (this.element.dataset.stripParams || '')
            .split(',')
            .map((param) => param.trim())
            .filter(Boolean);

        if (params.length === 0) {
            return;
        }

        const url = new URL(window.location.href);
        let changed = false;

        for (const param of params) {
            if (url.searchParams.has(param)) {
                url.searchParams.delete(param);
                changed = true;
            }
        }

        if (changed) {
            window.history.replaceState(null, '', url.toString());
        }
    }

    bindModalOpenButtons() {
        for (const el of document.querySelectorAll('[data-modal-open="' + this.element.id + '"]')) {
            el.addEventListener('click', () => this.showModal());
        }
    }

    getOpenedModal() {
        for (const controller of availableModalControllers) {
            if (controller.element.classList.contains('modal-show')) {
                return controller;
            }
        }

        return null;
    }

    showModal(callback = undefined) {
        if (!this.shouldShowOnceADayModal()) {
            return;
        }

        const currentModal = this.getOpenedModal();

        if (currentModal) {
            currentModal.hideModal(() => {
                this.showModalHandle(callback);
            });
        } else {
            this.showModalHandle(callback);
        }

        this.markOnceADayModalAsShown();
    }

    showModalHandle(callback) {
        if (!this.element.classList.contains('persistent-form-messages')) {
            this.clearModalFormInputs();
        }
        this.element.classList.add('modal-show');
        this.element.dispatchEvent(new CustomEvent('modal:shown'));

        let op = 0.1;
        this.element.style.display = 'block';
        let timer = setInterval(() => {
            if (op >= 1) {
                clearInterval(timer);

                if (typeof callback === 'function') {
                    callback();
                }
            }
            this.element.style.opacity = op;
            this.element.style.filter = 'alpha(opacity=' + op * 100 + ')';
            op += op * 0.1;
        }, 5);

        document.getElementsByTagName('html')[0].classList.add('modal-visible');
    }

    hideModal(callback) {
        this.element.classList.remove('modal-show');

        let op = 1;
        let timer = setInterval(() => {
            if (op <= 0.1) {
                clearInterval(timer);
                this.element.style.display = 'none';

                if (typeof callback === 'function') {
                    callback();
                }
            }
            this.element.style.opacity = op;
            this.element.style.filter = 'alpha(opacity=' + op * 100 + ')';
            op -= op * 0.1;
        }, 5);

        document.getElementsByTagName('html')[0].classList.remove('modal-visible');
    }

    clearModalFormInputs() {
        const forms = this.element.querySelectorAll('form');

        for (const form of forms) {
            const generalErrors = form.querySelectorAll('.general-error');
            for (const el of generalErrors) {
                el.remove();
            }

            const generalSuccess = form.querySelectorAll('.general-success');
            for (const el of generalSuccess) {
                el.remove();
            }

            const fields = form.querySelectorAll('.input');
            for (const el of fields) {
                el.classList.remove('invalid');

                if (el.classList.contains('clear-before') && !el.classList.contains('ignore-clear-before')) {
                    el.value = '';
                }

                if (el.hasAttribute('data-reset-to')) {
                    el.value = el.getAttribute('data-reset-to');
                }
            }

            const hiddenFields = form.querySelectorAll('.axios-hide');
            for (const el of hiddenFields) {
                el.classList.remove('d-none');
            }

            const messageFields = form.querySelectorAll('.input-error');
            for (const el of messageFields) {
                el.remove();
            }
        }
    }

    getStoredModalDates() {
        try {
            return JSON.parse(localStorage.getItem(MODALS_STORAGE_KEY)) || {};
        } catch (e) {
            return {};
        }
    }

    saveModalDates(dates) {
        localStorage.setItem(MODALS_STORAGE_KEY, JSON.stringify(dates));
    }

    shouldShowOnceADayModal() {
        const onceADayKey = this.element.dataset.showOnceADayKey;
        if (!onceADayKey) return true;

        const modalDates = this.getStoredModalDates();
        const lastShown = modalDates[onceADayKey];
        const today = new Date().toDateString();

        return lastShown !== today;
    }

    markOnceADayModalAsShown() {
        const onceADayKey = this.element.dataset.showOnceADayKey;
        if (!onceADayKey) return;

        const modalDates = this.getStoredModalDates();
        modalDates[onceADayKey] = new Date().toDateString();

        this.cleanUpOldEntries(modalDates);

        this.saveModalDates(modalDates);
    }

    cleanUpOldEntries(modalDates) {
        const today = new Date().toDateString();

        Object.keys(modalDates).forEach(key => {
            if (modalDates[key] !== today) {
                delete modalDates[key];
            }
        });
    }

    disconnect() {
        this.element.removeEventListener('modal:open', this._onElementOpen);
        this.element.removeEventListener('modal:close', this._onElementClose);

        document.removeEventListener('modal:open', this._onDocumentOpen);
        document.removeEventListener('modal:close', this._onDocumentClose);
    }
}
